import type { OperationalFact } from '../facts/operational-facts.ts';
import type { BusinessDaySnapshot } from '../business-day/business-day-authority.ts';
import type { TableAuthorityProjection } from '../table/table-authority.ts';
import { readAcceptedOrderEvidence, readClosedDayReceipt } from './source-evidence.ts';
import { rankProducts } from './comparison-ranking.ts';

export interface ReportingOrderBreakdownRow {
  readonly source: string;
  readonly channel: string;
  readonly orderCount: number;
  readonly grossOrderMinor: number;
}

export interface ReportingPaymentBreakdownRow {
  readonly method: string;
  readonly orderCount: number;
  readonly receivedMinor: number;
  readonly refundMinor: number;
  readonly correctionInMinor: number;
  readonly correctionOutMinor: number;
  readonly netMinor: number;
}

export interface ProductPerformanceRow {
  readonly productId: string;
  readonly customerName: string;
  readonly operationsName: string;
  readonly quantity: number;
  readonly lineAmountMinor: number;
  readonly rank: number;
}

export interface ProductPerformanceProjection {
  readonly sourceSafety: 'established' | 'source_not_established';
  readonly ranking: readonly ProductPerformanceRow[];
  readonly amountRanking: readonly ProductPerformanceRow[];
}

export interface BusinessDateProgressProjection {
  readonly businessDate: string;
  readonly cutoffAt: string;
  readonly turnoverMinor: number;
  readonly orderCount: number;
  readonly aovMinor: number | null;
  readonly productPerformance: ProductPerformanceProjection;
}

export interface CashPositionProjection {
  readonly openingCashMinor: number;
  readonly cashInMinor: number;
  readonly cashOutMinor: number;
  readonly cashPaymentNetMinor: number;
  readonly expectedCashMinor: number;
  readonly countedCashMinor: number | null;
  readonly varianceMinor: number | null;
}

export interface TodayOperationsProjection {
  readonly mode: 'LIVE';
  readonly businessDate: string;
  readonly turnoverMinor: number;
  readonly orderCount: number;
  readonly aovMinor: number | null;
  readonly receivedMinor: number;
  readonly refundMinor: number;
  readonly correctionInMinor: number;
  readonly correctionOutMinor: number;
  readonly netReceivedMinor: number;
  readonly orderBreakdown: readonly ReportingOrderBreakdownRow[];
  readonly paymentBreakdown: readonly ReportingPaymentBreakdownRow[];
  readonly openDineIn: {
    readonly sessionCount: number;
    readonly orderCount: number;
    readonly exposureMinor: number;
  };
  readonly incompleteOrderCount: number;
  readonly cashPosition: CashPositionProjection;
  readonly productPerformance: ProductPerformanceProjection;
  readonly unallocatedOrderAdjustmentMinor: number | null;
  readonly lastSuccessfulRefreshAt: string;
}

export interface PeriodReportProjection {
  readonly mode: 'FINAL';
  readonly historicalMode: 'AS_CLOSED' | 'RESTATED';
  readonly businessDates: readonly string[];
  readonly turnoverMinor: number;
  readonly orderCount: number;
  readonly aovMinor: number | null;
  readonly receivedMinor: number;
  readonly refundMinor: number;
  readonly correctionInMinor: number;
  readonly correctionOutMinor: number;
  readonly netReceivedMinor: number;
  readonly restatedPerformanceMinor: number | null;
  readonly productPerformance: ProductPerformanceProjection;
}

interface MutablePaymentBucket {
  readonly method: string;
  readonly orderIds: Set<string>;
  receivedMinor: number;
  refundMinor: number;
  correctionInMinor: number;
  correctionOutMinor: number;
}

interface MutableOrderBucket {
  readonly source: string;
  readonly channel: string;
  orderCount: number;
  grossOrderMinor: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const isNonNegativeInteger = (value: unknown): value is number => Number.isSafeInteger(value) && (value as number) >= 0;
const isPositiveInteger = (value: unknown): value is number => Number.isSafeInteger(value) && (value as number) > 0;
const text = (value: unknown): string | null => typeof value === 'string' && value.trim() ? value.trim() : null;

const factsThrough = (facts: readonly OperationalFact[], businessDate: string, now: string): readonly OperationalFact[] => {
  const cutoff = Date.parse(now);
  if (!Number.isFinite(cutoff)) throw new Error('REPORTING_NOW_INVALID');
  return facts.filter((fact) => {
    if (fact.businessDate !== businessDate) return false;
    const occurredAt = Date.parse(fact.occurredAt);
    if (!Number.isFinite(occurredAt)) throw new Error('REPORTING_FACT_TIME_INVALID');
    return occurredAt <= cutoff;
  });
};

const receivedAmount = (payload: Record<string, unknown>): number | null => {
  if (isNonNegativeInteger(payload.amountMinor)) return payload.amountMinor;
  if (isNonNegativeInteger(payload.tenderedMinor) && isNonNegativeInteger(payload.changeMinor)) {
    const net = payload.tenderedMinor - payload.changeMinor;
    return net >= 0 ? net : null;
  }
  return null;
};

const projectPayments = (facts: readonly OperationalFact[]) => {
  const buckets = new Map<string, MutablePaymentBucket>();
  const netByOrder = new Map<string, number>();
  for (const fact of facts) {
    if (fact.factType !== 'payment.received' && fact.factType !== 'payment.refunded' && fact.factType !== 'payment.corrected') continue;
    if (!isRecord(fact.payload)) throw new Error('REPORTING_PAYMENT_FACT_INVALID');
    const method = text(fact.payload.method);
    if (!method) throw new Error('REPORTING_PAYMENT_FACT_INVALID');
    const key = method.toUpperCase();
    const bucket = buckets.get(key) ?? {
      method,
      orderIds: new Set<string>(),
      receivedMinor: 0,
      refundMinor: 0,
      correctionInMinor: 0,
      correctionOutMinor: 0,
    };
    bucket.orderIds.add(fact.aggregateId);
    let delta = 0;
    if (fact.factType === 'payment.received') {
      const amount = receivedAmount(fact.payload);
      if (amount === null) throw new Error('REPORTING_PAYMENT_FACT_INVALID');
      bucket.receivedMinor += amount;
      delta += amount;
    } else {
      const amount = fact.payload.amountMinor;
      if (!isPositiveInteger(amount)) throw new Error('REPORTING_PAYMENT_FACT_INVALID');
      if (fact.factType === 'payment.refunded') {
        bucket.refundMinor += amount;
        delta -= amount;
      } else if (fact.payload.direction === 'in') {
        bucket.correctionInMinor += amount;
        delta += amount;
      } else if (fact.payload.direction === 'out') {
        bucket.correctionOutMinor += amount;
        delta -= amount;
      } else {
        throw new Error('REPORTING_PAYMENT_FACT_INVALID');
      }
    }
    buckets.set(key, bucket);
    netByOrder.set(fact.aggregateId, (netByOrder.get(fact.aggregateId) ?? 0) + delta);
  }
  const rows = [...buckets.values()]
    .map((bucket): ReportingPaymentBreakdownRow => ({
      method: bucket.method,
      orderCount: bucket.orderIds.size,
      receivedMinor: bucket.receivedMinor,
      refundMinor: bucket.refundMinor,
      correctionInMinor: bucket.correctionInMinor,
      correctionOutMinor: bucket.correctionOutMinor,
      netMinor: bucket.receivedMinor - bucket.refundMinor + bucket.correctionInMinor - bucket.correctionOutMinor,
    }))
    .sort((left, right) => left.method.localeCompare(right.method));
  return { rows, netByOrder };
};

const productPerformanceFromAccepted = (accepted: ReturnType<typeof readAcceptedOrderEvidence>[]): {
  readonly productPerformance: ProductPerformanceProjection;
  readonly unallocatedOrderAdjustmentMinor: number | null;
} => {
  if (accepted.some((order) => order.productSourceSafety !== 'established')) {
    return {
      productPerformance: { sourceSafety: 'source_not_established', ranking: [], amountRanking: [] },
      unallocatedOrderAdjustmentMinor: null,
    };
  }
  const products = new Map<string, {
    productId: string;
    customerName: string;
    operationsName: string;
    quantity: number;
    lineAmountMinor: number;
  }>();
  let lineGross = 0;
  let turnover = 0;
  for (const order of accepted) {
    turnover += order.totalMinor;
    const lineById = new Map(order.lines.map((line) => [line.lineId, line]));
    for (const pricing of order.pricingLines) {
      const line = lineById.get(pricing.lineId);
      if (!line) throw new Error('REPORTING_PRODUCT_EVIDENCE_INVALID');
      lineGross += pricing.lineSubtotalMinor;
      const current = products.get(pricing.productId) ?? {
        productId: pricing.productId,
        customerName: line.productSnapshot.customerName,
        operationsName: line.productSnapshot.operationsName,
        quantity: 0,
        lineAmountMinor: 0,
      };
      current.quantity += pricing.quantity;
      current.lineAmountMinor += pricing.lineSubtotalMinor;
      products.set(current.productId, current);
    }
  }
  const quantityRankByProduct = new Map(rankProducts([...products.values()].map((row) => ({ productId: row.productId, value: row.quantity }))).map((row) => [row.productId, row.rank]));
  const amountRankByProduct = new Map(rankProducts([...products.values()].map((row) => ({ productId: row.productId, value: row.lineAmountMinor }))).map((row) => [row.productId, row.rank]));
  const ranking = [...products.values()]
    .map((row): ProductPerformanceRow => ({ ...row, rank: quantityRankByProduct.get(row.productId)! }))
    .sort((left, right) => left.rank - right.rank || left.productId.localeCompare(right.productId));
  const amountRanking = [...products.values()]
    .map((row): ProductPerformanceRow => ({ ...row, rank: amountRankByProduct.get(row.productId)! }))
    .sort((left, right) => left.rank - right.rank || left.productId.localeCompare(right.productId));
  return {
    productPerformance: { sourceSafety: 'established', ranking, amountRanking },
    unallocatedOrderAdjustmentMinor: turnover - lineGross,
  };
};

export function projectBusinessDateProgress(input: {
  readonly facts: readonly OperationalFact[];
  readonly businessDate: string;
  readonly cutoffAt: string;
}): BusinessDateProgressProjection {
  const facts = factsThrough(input.facts, input.businessDate, input.cutoffAt);
  const accepted = facts.filter((fact) => fact.factType === 'order.accepted').map(readAcceptedOrderEvidence);
  const seen = new Set<string>();
  for (const order of accepted) {
    if (seen.has(order.orderId)) throw new Error('REPORTING_ACCEPTED_ORDER_DUPLICATE');
    seen.add(order.orderId);
  }
  const turnoverMinor = accepted.reduce((sum, order) => sum + order.totalMinor, 0);
  const product = productPerformanceFromAccepted(accepted).productPerformance;
  return {
    businessDate: input.businessDate,
    cutoffAt: input.cutoffAt,
    turnoverMinor,
    orderCount: accepted.length,
    aovMinor: accepted.length ? turnoverMinor / accepted.length : null,
    productPerformance: product,
  };
}

export function projectTodayOperations(input: {
  readonly facts: readonly OperationalFact[];
  readonly businessDay: BusinessDaySnapshot;
  readonly table: TableAuthorityProjection;
  readonly now: string;
}): TodayOperationsProjection {
  if (input.businessDay.status !== 'open') throw new Error('REPORTING_LIVE_BUSINESS_DAY_REQUIRED');
  if (input.table.businessDate !== input.businessDay.businessDate || input.table.storeId !== input.businessDay.storeId) {
    throw new Error('REPORTING_TABLE_SCOPE_MISMATCH');
  }
  const facts = factsThrough(input.facts, input.businessDay.businessDate, input.now);
  const accepted = facts.filter((fact) => fact.factType === 'order.accepted').map(readAcceptedOrderEvidence);
  const acceptedById = new Map<string, ReturnType<typeof readAcceptedOrderEvidence>>();
  for (const order of accepted) {
    if (acceptedById.has(order.orderId)) throw new Error('REPORTING_ACCEPTED_ORDER_DUPLICATE');
    acceptedById.set(order.orderId, order);
  }
  const turnoverMinor = accepted.reduce((sum, order) => sum + order.totalMinor, 0);
  const orderBuckets = new Map<string, MutableOrderBucket>();
  for (const order of accepted) {
    const key = `${order.source}:${order.channel}`;
    const current = orderBuckets.get(key) ?? { source: order.source, channel: order.channel, orderCount: 0, grossOrderMinor: 0 };
    current.orderCount += 1;
    current.grossOrderMinor += order.totalMinor;
    orderBuckets.set(key, current);
  }
  const orderBreakdown = [...orderBuckets.values()].map((row) => ({ ...row })).sort((left, right) => left.source.localeCompare(right.source) || left.channel.localeCompare(right.channel));
  const payment = projectPayments(facts);
  const receivedMinor = payment.rows.reduce((sum, row) => sum + row.receivedMinor, 0);
  const refundMinor = payment.rows.reduce((sum, row) => sum + row.refundMinor, 0);
  const correctionInMinor = payment.rows.reduce((sum, row) => sum + row.correctionInMinor, 0);
  const correctionOutMinor = payment.rows.reduce((sum, row) => sum + row.correctionOutMinor, 0);
  const netReceivedMinor = receivedMinor - refundMinor + correctionInMinor - correctionOutMinor;

  const openSessions = input.table.sessions.filter((session) => session.status !== 'closed');
  const openOrderIds = new Set<string>();
  let exposureMinor = 0;
  let exposedSessions = 0;
  for (const session of openSessions) {
    let sessionExposure = 0;
    for (const orderId of session.orderIds) {
      const order = acceptedById.get(orderId);
      if (!order) continue;
      const outstanding = Math.max(0, order.totalMinor - (payment.netByOrder.get(orderId) ?? 0));
      if (outstanding > 0) {
        openOrderIds.add(orderId);
        sessionExposure += outstanding;
      }
    }
    if (sessionExposure > 0) {
      exposedSessions += 1;
      exposureMinor += sessionExposure;
    }
  }

  const completed = new Set(facts.filter((fact) => fact.factType === 'fulfillment.completed').map((fact) => fact.aggregateId));
  const incompleteOrderCount = accepted.filter((order) => !completed.has(order.orderId)).length;

  const shift = input.businessDay.cashShift;
  const cashInMinor = shift.movements.filter((movement) => movement.direction === 'in').reduce((sum, movement) => sum + movement.amountMinor, 0);
  const cashOutMinor = shift.movements.filter((movement) => movement.direction === 'out').reduce((sum, movement) => sum + movement.amountMinor, 0);
  const cashRow = payment.rows.find((row) => row.method.trim().toUpperCase() === 'CASH');
  const cashPaymentNetMinor = cashRow?.netMinor ?? 0;
  const expectedCashMinor = shift.openingCashMinor + cashInMinor - cashOutMinor + cashPaymentNetMinor;
  const product = productPerformanceFromAccepted(accepted);

  return {
    mode: 'LIVE',
    businessDate: input.businessDay.businessDate,
    turnoverMinor,
    orderCount: accepted.length,
    aovMinor: accepted.length ? turnoverMinor / accepted.length : null,
    receivedMinor,
    refundMinor,
    correctionInMinor,
    correctionOutMinor,
    netReceivedMinor,
    orderBreakdown,
    paymentBreakdown: payment.rows,
    openDineIn: { sessionCount: exposedSessions, orderCount: openOrderIds.size, exposureMinor },
    incompleteOrderCount,
    cashPosition: {
      openingCashMinor: shift.openingCashMinor,
      cashInMinor,
      cashOutMinor,
      cashPaymentNetMinor,
      expectedCashMinor,
      countedCashMinor: null,
      varianceMinor: null,
    },
    productPerformance: product.productPerformance,
    unallocatedOrderAdjustmentMinor: product.unallocatedOrderAdjustmentMinor,
    lastSuccessfulRefreshAt: input.now,
  };
}

const linkedAdjustmentToOriginal = (fact: OperationalFact, targetDates: ReadonlySet<string>): number => {
  if (fact.factType !== 'payment.refunded' && fact.factType !== 'payment.corrected') return 0;
  if (!isRecord(fact.payload)) return 0;
  const originalDate = text(fact.payload.originalOrderBusinessDate);
  if (!originalDate || !targetDates.has(originalDate)) return 0;
  const amount = fact.payload.amountMinor;
  if (!isPositiveInteger(amount)) throw new Error('REPORTING_ADJUSTMENT_FACT_INVALID');
  if (fact.factType === 'payment.refunded') return -amount;
  if (fact.payload.direction === 'in') return amount;
  if (fact.payload.direction === 'out') return -amount;
  throw new Error('REPORTING_ADJUSTMENT_FACT_INVALID');
};

export function projectPeriodReport(input: {
  readonly facts: readonly OperationalFact[];
  readonly businessDates: readonly string[];
  readonly historicalMode: 'AS_CLOSED' | 'RESTATED';
}): PeriodReportProjection {
  const businessDates = [...new Set(input.businessDates)].sort();
  if (businessDates.length === 0) throw new Error('REPORTING_PERIOD_DATES_REQUIRED');
  const targetDates = new Set(businessDates);
  const receipts = new Map<string, ReturnType<typeof readClosedDayReceipt>>();
  for (const fact of input.facts) {
    if (fact.factType !== 'day_close.receipt' || !targetDates.has(fact.businessDate)) continue;
    const receipt = readClosedDayReceipt(fact);
    if (receipts.has(fact.businessDate)) throw new Error('REPORTING_FINAL_RECEIPT_DUPLICATE');
    receipts.set(fact.businessDate, receipt);
  }
  for (const date of businessDates) if (!receipts.has(date)) throw new Error(`REPORTING_FINAL_RECEIPT_REQUIRED:${date}`);

  const values = businessDates.map((date) => receipts.get(date)!.receipt);
  const turnoverMinor = values.reduce((sum, receipt) => sum + receipt.grossOrderMinor, 0);
  const orderCount = values.reduce((sum, receipt) => sum + receipt.totalOrderCount, 0);
  const receivedMinor = values.reduce((sum, receipt) => sum + receipt.receivedMinor, 0);
  const refundMinor = values.reduce((sum, receipt) => sum + receipt.refundMinor, 0);
  const correctionInMinor = values.reduce((sum, receipt) => sum + receipt.correctionInMinor, 0);
  const correctionOutMinor = values.reduce((sum, receipt) => sum + receipt.correctionOutMinor, 0);
  const netReceivedMinor = values.reduce((sum, receipt) => sum + receipt.netReceivedMinor, 0);

  const accepted = input.facts
    .filter((fact) => fact.factType === 'order.accepted' && targetDates.has(fact.businessDate))
    .map(readAcceptedOrderEvidence);
  const uniqueAccepted = new Map<string, ReturnType<typeof readAcceptedOrderEvidence>>();
  for (const order of accepted) uniqueAccepted.set(`${order.businessDate}:${order.orderId}`, order);
  const sourceSafe = uniqueAccepted.size === orderCount && [...uniqueAccepted.values()].every((order) => order.productSourceSafety === 'established');
  const product = sourceSafe
    ? productPerformanceFromAccepted([...uniqueAccepted.values()]).productPerformance
    : { sourceSafety: 'source_not_established' as const, ranking: [] as readonly ProductPerformanceRow[], amountRanking: [] as readonly ProductPerformanceRow[] };

  let restatedPerformanceMinor: number | null = null;
  if (input.historicalMode === 'RESTATED') {
    restatedPerformanceMinor = turnoverMinor - refundMinor + correctionInMinor - correctionOutMinor;
    for (const fact of input.facts) {
      if (targetDates.has(fact.businessDate)) continue;
      restatedPerformanceMinor += linkedAdjustmentToOriginal(fact, targetDates);
    }
  }

  return {
    mode: 'FINAL',
    historicalMode: input.historicalMode,
    businessDates,
    turnoverMinor,
    orderCount,
    aovMinor: orderCount ? turnoverMinor / orderCount : null,
    receivedMinor,
    refundMinor,
    correctionInMinor,
    correctionOutMinor,
    netReceivedMinor,
    restatedPerformanceMinor,
    productPerformance: product,
  };
}